<template>
  <div id="dashboardCoreOrders">
    <WizardAlert section="dashboardCoreOrders" alertClass="my-4">
      <template v-slot:text>
        <p>
          {{ $t('views.dashboardDetail.coreOrders.wizard.a') }}
        </p>
        <p>
          {{ $t('views.dashboardDetail.coreOrders.wizard.b') }}
        </p>
        <p>
          {{ $t('views.dashboardDetail.coreOrders.wizard.b1') }}
        </p>
        <p>
          {{ $t('views.dashboardDetail.coreOrders.wizard.c') }}
          <a href="https://www.filios.app/guia-de-fiscalidad/" target="_blank">
            {{ $t('views.dashboardDetail.coreOrders.wizard.d') }}
          </a>
        </p>
      </template>
    </WizardAlert>

    <v-card class="px-5 py-2 mt-4 mb-5 elevation-0">
      <div :class="{ 'sticky-filters': user.preferences.stickyFilters }">
        <CommonFilters type="coreOrder" @filterElements="filterElements">
          <template v-slot:add-button>
            <WizardAlertActivator section="dashboardCoreOrders" />
          </template>
        </CommonFilters>
      </div>

      <v-data-table
        :headers="headers"
        :items="items"
        :options.sync="options"
        :server-items-length="totalItems"
        sort-by="date"
        :sort-desc="true"
        class="dashboard-core-orders-table elevation-0 mt-2"
        mobile-breakpoint="0"
        :loading="loading"
        :no-data-text="$t('views.dashboardDetail.coreOrders.coreOrdersTable.noOrders')"
        :item-class="itemRowBackground"
        :footer-props="{
          'items-per-page-options': defaultPaginationItemsPerPageOptions,
        }"
        :items-per-page="user.preferences.denseTables ? 15 : 10"
        :dense="user.preferences.denseTables"
        @update:options="updateOptions"
      >
        <template v-slot:item.type="{ item }">
          <OrderTableTypeIcon :item="item" />
        </template>
        <template v-slot:item.date="{ item }">
          {{ getDateUserFormat(item.date) }}
        </template>
        <template v-slot:item.broker.alias="{ item }">
          <div class="items-flex">
            <TableBroker :broker="item.broker" />
            <OrderChangeHistory :changeHistory="item.changeHistory" field="broker" />
          </div>
        </template>
        <template v-slot:item.position.ticker="{ item }">
          <div class="items-flex">
            <TableTicker :position="item.position" />
            <div
              v-if="item.position.corePendingRecreate || item.position.coreStatus !== 'recreated'"
              class="ml-3"
            >
              <TableCoreRecreating :text="$t('views.common.tableCoreRecreating.text')" />
            </div>
          </div>
        </template>
        <template v-slot:item.from="{ item }">
          <template v-if="item.createdFromFxOrder">
            <span v-if="item.createdFromFxOrderFrom.model === 'dividend'">
              {{
                $t(
                  `views.dashboardDetail.coreOrders.coreOrdersTable.fromFxOrder.createdFrom.model.dividend.${
                    item.createdFromFxOrderFrom.dividend.type || 'stockDividend'
                  }`
                )
              }}
            </span>
            <span v-if="item.createdFromFxOrderFrom.model === 'order'">
              {{
                $t(
                  `views.dashboardDetail.coreOrders.coreOrdersTable.fromFxOrder.createdFrom.model.order.${item.createdFromFxOrderFrom.order.type}`
                )
              }}
              {{
                $t(
                  `views.dashboardDetail.coreOrders.coreOrdersTable.fromFxOrder.createdFrom.model.order.orderType.${item.createdFromFxOrderFrom.position.type}`
                )
              }}
            </span>
            ({{ item.createdFromFxOrderFrom.position.ticker }})
          </template>
          <template v-else>
            {{
              $t(
                `views.dashboardDetail.coreOrders.coreOrdersTable.fromFxOrder.createdFrom.model.order.${item.createdFromOrder.type}`
              )
            }}
            {{
              $t(
                `views.dashboardDetail.coreOrders.coreOrdersTable.fromFxOrder.createdFrom.model.order.orderType.${item.createdFromOrder.position.type}`
              )
            }}
            ({{ item.createdFromOrder.position.ticker }})
          </template>
        </template>
        <template v-slot:item.positionNumber="{ item }">
          <div class="items-flex justify-end">
            <currency
              :value="item.positionNumber"
              :currency="item.position.type === 'cash' ? item.position.ticker : undefined"
              :decimals="7"
              :text-green="item.type === 'buy'"
              :text-red="item.type === 'sell'"
              :negative="item.type === 'sell'"
              strong
            />
            <OrderChangeHistory :changeHistory="item.changeHistory" field="positionNumber" />
          </div>
        </template>
        <template v-slot:item.accPositionNumber="{ item }">
          <div class="items-flex justify-end">
            <currency
              :value="item.accPositionNumber"
              :currency="item.position.type === 'cash' ? item.position.ticker : undefined"
              :text-green="item.accPositionNumber > 0"
              :text-red="item.accPositionNumber < 0"
              :decimals="item.positionType === 'crypto' ? 7 : 2"
            />
          </div>
        </template>
        <template v-slot:item.brokerAccPositionNumber="{ item }">
          <div class="items-flex justify-end">
            <currency
              :value="item.brokerAccPositionNumber"
              :currency="item.position.type === 'cash' ? item.position.ticker : undefined"
              :text-green="item.brokerAccPositionNumber > 0"
              :text-red="item.brokerAccPositionNumber < 0"
              :decimals="item.positionType === 'crypto' ? 7 : 2"
            />
          </div>
        </template>
        <template v-slot:item.price="{ item }">
          <div class="items-flex justify-end">
            <template v-if="item.price !== null">
              <currency :value="item.price" :currency="item.position.currency" :decimals="3" />
              <OrderChangeHistory
                :changeHistory="item.changeHistory"
                :position="item.position"
                field="price"
              />
            </template>
            <WarningIcon
              v-else
              :text="$t('views.dashboardDetail.coreOrders.coreOrdersTable.noPrice')"
            />
          </div>
        </template>
        <template v-slot:item.total="{ item }">
          <currency v-if="item.total" :value="item.total" :currency="item.position.currency" />
          <span v-else></span>
        </template>
        <template v-slot:item.totalBaseCurrency="{ item }">
          <currency
            v-if="item.totalBaseCurrency"
            :value="item.totalBaseCurrency"
            :currency="user.baseCurrency"
          />
          <span v-else></span>
        </template>
        <template v-slot:item.comission="{ item }">
          <div class="items-flex justify-end">
            <currency
              v-if="item.comission && item.comission !== 0"
              :value="item.comission"
              :text-red="!!item.comission"
              :currency="item.comissionCurrency"
              :decimals="5"
            />
            <span v-else></span>
            <OrderChangeHistory :changeHistory="item.changeHistory" field="comission" />
          </div>
        </template>
        <template v-slot:item.exchangeRate="{ item }">
          <div class="items-flex justify-end">
            <TableExchangeRate :item="item" type="order" />
            <div class="table-exchange-rate-change-history">
              <OrderChangeHistory :changeHistory="item.changeHistory" field="exchangeRate" />
            </div>
          </div>
        </template>
        <template v-slot:item.totalWithComissionBaseCurrency="{ item }">
          <currency
            v-if="item.totalBaseCurrency"
            :value="item.totalWithComissionBaseCurrency"
            :currency="user.baseCurrency"
            strong
          />
          <span v-else></span>
        </template>
        <template v-slot:item.fifo.position.profitWithComissionBaseCurrency="{ item }">
          <currency
            v-if="item.fifo.position.profitWithComissionBaseCurrency"
            :value="item.fifo.position.profitWithComissionBaseCurrency"
            :currency="user.baseCurrency"
            :text-green="item.fifo.position.profitWithComissionBaseCurrency > 0"
            :text-red="item.fifo.position.profitWithComissionBaseCurrency < 0"
            :negative="item.fifo.position.profitWithComissionBaseCurrency < 0"
            :strong="!item.notGp"
          />
          <span v-else></span>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import { get } from 'lodash'
import { mapState } from 'vuex'
import { getCoreOrders } from '@/lib/api.js'
import { getDateUserFormat } from '@/lib/utils/date.js'
import { getCurrencySymbol } from '@/lib/utils/currencies.js'
import CommonFilters from '@/views/components/common/CommonFilters'
import WizardAlert from '@/views/components/common/WizardAlert'
import WizardAlertActivator from '@/views/components/common/WizardAlertActivator'
import Currency from '@/views/components/common/Currency'
import OrderTableTypeIcon from '@/views/components/common/orders/OrderTableTypeIcon'
import TableTicker from '@/views/components/common/TableTicker'
import TableBroker from '@/views/components/common/TableBroker'
import TableExchangeRate from '@/views/components/common/TableExchangeRate'
import OrderChangeHistory from '@/views/components/common/OrderChangeHistory'
import TableCoreRecreating from '@/views/components/common/positions/TableCoreRecreating'
import WarningIcon from '@/views/components/common/WarningIcon.vue'
import tableMixin from '@/views/components/core/tableMixin.js'

export default {
  components: {
    CommonFilters,
    WizardAlert,
    WizardAlertActivator,
    Currency,
    OrderTableTypeIcon,
    TableTicker,
    TableBroker,
    TableExchangeRate,
    OrderChangeHistory,
    TableCoreRecreating,
    WarningIcon,
  },
  mixins: [tableMixin],
  data: () => ({
    filters: {},
    loadTimeout: null,
  }),
  computed: {
    ...mapState(['user']),
    defaultHeaders() {
      return [
        { text: '', value: 'type', sortable: false },
        { text: this.$t('views.orders.ordersTable.date'), value: 'date' },
        {
          text: this.$t('views.orders.ordersTable.broker'),
          value: 'broker.alias',
          sortable: false,
        },
        {
          text: this.$t('views.orders.ordersTable.ticker'),
          value: 'position.ticker',
          sortable: false,
        },
        { text: this.$t('views.orders.ordersTable.from'), value: 'from', sortable: false },
        {
          text: this.$t('views.orders.ordersTable.positionNumber'),
          value: 'positionNumber',
          align: 'right',
        },
        {
          text: this.$t('views.orders.ordersTable.accPositionNumber'),
          value: 'accPositionNumber',
          align: 'right',
          sortable: false,
        },
        {
          text: this.$t('views.orders.ordersTable.brokerAccPositionNumber'),
          value: 'brokerAccPositionNumber',
          align: 'right',
          sortable: false,
        },
        {
          text: this.$t('views.orders.ordersTable.price'),
          value: 'price',
          align: 'right',
          sortable: false,
        },
        { text: this.$t('views.orders.ordersTable.total'), value: 'total', align: 'right' },
        { text: this.$t('views.orders.ordersTable.comission'), value: 'comission', align: 'right' },
        {
          text: this.$t('views.orders.ordersTable.exchangeRate'),
          value: 'exchangeRate',
          align: 'right',
          sortable: false,
        },
        {
          text: `${this.$t('views.orders.ordersTable.total')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'totalBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t('views.orders.ordersTable.totalWithComission')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'totalWithComissionBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t('views.orders.ordersTable.profitWithComission')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'fifo.position.profitWithComissionBaseCurrency',
          align: 'right',
        },
      ]
    },
    somePendingRecreatePosition() {
      return this.items && this.items.some((item) => item.position.pendingRecreate)
    },
  },
  async destroyed() {
    clearTimeout(this.loadTimeout)
  },
  methods: {
    getDateUserFormat,
    itemRowBackground(item) {
      if (item.position.pendingRecreate) return 'row-recreating'
      else if (item.exchangeRate === null) return 'row-advise'
      else if (item.price === null) return 'row-advise'
      else if (get(item, 'forkedFrom', null) || get(item, 'forkedTo', null)) return 'row-fork-from'
    },
    async loadItems() {
      this.loading = true
      try {
        const filter = JSON.parse(JSON.stringify(this.filters))
        const { page, limit, sort } = this.getTablePaginationOptions(this.options)
        const response = await getCoreOrders({ filter, page, limit, sort })
        this.setTablePaginatedResponse(response)

        clearTimeout(this.loadTimeout)
        if (this.somePendingRecreatePosition) {
          this.loadTimeout = setTimeout(() => {
            this.loadItems()
          }, 5000)
        }
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
      }
      this.loading = false
    },
  },
}
</script>

<style lang="scss">
.dashboard-core-orders-table {
  .items-flex {
    display: flex;
    &.justify-end {
      justify-content: flex-end;
    }
  }
  .row-fork-from {
    background: #fafafa;
  }
  .table-exchange-rate-change-history > div > .v-avatar {
    margin-bottom: 1px;
  }
}
</style>
